import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Header } from '../components/Header';
import axios from 'axios';
import { Shield, Target, TrendingUp, Lock, ChevronRight, PlayCircle, Loader2, BookOpen } from 'lucide-react';
import { PendingBanner, UnlockDialog, isLockedStatus } from '../components/UnlockGate';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PHASE_META = {
    safe_start: { icon: Shield, color: '#3B82F6', bgClass: 'badge-phase-1' },
    building_consistency: { icon: Target, color: '#8B5CF6', bgClass: 'badge-phase-2' },
    capital_confidence: { icon: TrendingUp, color: '#D4AF37', bgClass: 'badge-phase-3' }
};

export default function CoursesPage() {
    const { user } = useAuth();
    const { t, i18n } = useTranslation();
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activePhase, setActivePhase] = useState('all');
    const [unlockOpen, setUnlockOpen] = useState(false);
    const isPending = isLockedStatus(user);

    useEffect(() => {
        fetchCourses();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n.language]);

    const fetchCourses = async () => {
        try {
            const { data } = await axios.get(`${API}/courses`, { 
                params: { lang: i18n.language },
                withCredentials: true 
            });
            setCourses(data);
        } catch (e) {
            console.error('Error fetching courses:', e);
        } finally {
            setLoading(false);
        }
    };

    const filteredCourses = activePhase === 'all' 
        ? courses 
        : courses.filter(c => c.phase === activePhase);

    if (loading) {
        return (
            <div className="min-h-screen" style={{ backgroundColor: 'var(--background-base)' }}>
                <Header />
                <div className="flex items-center justify-center h-[60vh]">
                    <Loader2 className="w-10 h-10 animate-spin" style={{ color: 'var(--primary-color)' }} />
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen" style={{ backgroundColor: 'var(--background-base)' }}>
            <Header />
            
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Page Header */}
                <div className="mb-8">
                    <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2" style={{ fontFamily: "'Outfit', sans-serif", color: 'var(--text-primary)' }} data-testid="courses-page-title">
                        {t('coursesPage.title')}
                    </h1>
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                        {t('coursesPage.subtitle')}
                    </p>
                </div>

                {isPending && <PendingBanner user={user} onUnlock={() => setUnlockOpen(true)} />}

                {/* Phase Filter Tabs */}
                <div className="flex flex-wrap gap-2 mb-8" data-testid="phase-filter-tabs">
                    <button
                        onClick={() => setActivePhase('all')}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activePhase === 'all' ? 'text-white' : ''}`}
                        style={{
                            backgroundColor: activePhase === 'all' ? 'var(--primary-color)' : 'var(--background-elevated)',
                            color: activePhase === 'all' ? 'var(--primary-text)' : 'var(--text-secondary)',
                            border: `1px solid ${activePhase === 'all' ? 'var(--primary-color)' : 'var(--border-subtle)'}`
                        }}
                        data-testid="filter-all"
                    >
                        {t('coursesPage.allPhases')}
                    </button>
                    {Object.entries(PHASE_META).map(([key, meta]) => (
                        <button
                            key={key}
                            onClick={() => setActivePhase(key)}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2`}
                            style={{
                                backgroundColor: activePhase === key ? `${meta.color}20` : 'var(--background-elevated)',
                                color: activePhase === key ? meta.color : 'var(--text-secondary)',
                                border: `1px solid ${activePhase === key ? `${meta.color}40` : 'var(--border-subtle)'}`
                            }}
                            data-testid={`filter-${key}`}
                        >
                            {t(`phases.${key}.name`)}
                        </button>
                    ))}
                </div>

                {/* Courses Grid */}
                {filteredCourses.length === 0 ? (
                    <div className="card-surface p-12 text-center" data-testid="no-courses-message">
                        <BookOpen className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--text-muted)' }} />
                        <p className="text-lg font-medium mb-2" style={{ color: 'var(--text-primary)' }}>{t('coursesPage.noCourses')}</p>
                        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                            {activePhase !== 'all' ? t('coursesPage.noCoursesDesc') : t('dashboard.noCoursesDesc')}
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredCourses.map((course) => {
                            const phaseInfo = PHASE_META[course.phase];
                            const isAccessible = course.accessible;
                            const Icon = phaseInfo?.icon || Shield;
                            
                            return (
                                <Link
                                    key={course.id}
                                    to={isAccessible ? `/courses/${course.id}` : '#'}
                                    className={`card-surface p-6 block transition-all duration-200 group ${isAccessible ? 'hover:border-[var(--border-highlight)] cursor-pointer' : isPending ? 'opacity-70 cursor-pointer hover:border-[var(--border-highlight)]' : 'opacity-60 cursor-not-allowed'}`}
                                    onClick={(e) => {
                                        if (!isAccessible) {
                                            e.preventDefault();
                                            if (isPending) setUnlockOpen(true);
                                        }
                                    }}
                                    data-testid={`course-card-${course.id}`}
                                >
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${phaseInfo?.color}20`, border: `1px solid ${phaseInfo?.color}40` }}>
                                            <Icon className="w-6 h-6" style={{ color: phaseInfo?.color }} />
                                        </div>
                                        {!isAccessible && (
                                            <div className="flex items-center gap-1 px-2 py-1 rounded text-xs" style={{ backgroundColor: 'var(--background-elevated)', color: 'var(--text-muted)' }}>
                                                <Lock className="w-3 h-3" />
                                                {t('coursesPage.locked')}
                                            </div>
                                        )}
                                    </div>

                                    <span className={`${phaseInfo?.bgClass} px-2 py-1 rounded text-xs font-semibold inline-block mb-3`}>
                                        {t(`phases.${course.phase}.name`)}
                                    </span>
                                    
                                    <h3 className="text-lg font-semibold mb-2 line-clamp-2" style={{ fontFamily: "'Outfit', sans-serif", color: 'var(--text-primary)' }}>
                                        {course.title}
                                    </h3>
                                    
                                    <p className="text-sm mb-4 line-clamp-3" style={{ color: 'var(--text-secondary)' }}>
                                        {course.description}
                                    </p>
                                    
                                    <div className="flex items-center justify-between pt-4 border-t" style={{ borderColor: 'var(--border-subtle)' }}>
                                        <span className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-muted)' }}>
                                            <PlayCircle className="w-4 h-4" />
                                            {course.lesson_count || 0} {t('coursesPage.lessons')}
                                        </span>
                                        {isAccessible && (
                                            <ChevronRight className="w-5 h-5 transition-transform group-hover:translate-x-1 rtl-flip" style={{ color: 'var(--primary-color)' }} />
                                        )}
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                )}
            </main>
            <UnlockDialog open={unlockOpen} onOpenChange={setUnlockOpen} />
        </div>
    );
}
